// Date/time formatters ported from design_handoff/data.jsx.

import { aiSpecies, type Analysis } from "../api/types";

export const SPECIES_LABEL = {
  "moose": "Moose",
  "roe-deer": "Roe Deer",
  "wild-boar": "Wild Boar",
  "other": "Other",
} as const;

export const SPECIES_ORDER = ["moose", "roe-deer", "wild-boar", "other"] as const;

// Filter chips mirror the on-card chip vocabulary one-to-one — same four
// labels, same semantics, so toggling a filter and reading a card's chip
// never contradict each other.
export const SPECIES_FILTER_ORDER = SPECIES_ORDER;

export const SPECIES_FILTER_LABEL = SPECIES_LABEL;

export function fmtTime(d: Date): string {
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, "0"))
    .join(":");
}

export function fmtDate(d: Date): string {
  return [
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
    d.getFullYear(),
  ].join("-");
}

export function fmtDateLong(d: Date): string {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function fmtRelative(d: Date): string {
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 172_800) return "yesterday";
  return `${Math.floor(diff / 86_400)} days ago`;
}

/**
 * A file size a person can read at a glance.
 *
 * <p>Decimal units (kB = 1000), not binary. This number sits next to a filename
 * in a list, where it answers "is this the big scan or the small one" — and the
 * figure the operating system shows for the same file, which is what it will be
 * compared against, is decimal on macOS and on every phone.
 *
 * <p>One decimal below 10 and none above, so the column stays the same width and
 * "9.4 MB" does not sit under "11.2 MB" claiming more precision than anybody
 * needs.
 */
export function fmtBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1000) return `${n} B`;
  const units = ["kB", "MB", "GB", "TB"];
  let value = n / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

export function endOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
}

export function startOfMonth(d: Date): Date {
  const c = new Date(d.getFullYear(), d.getMonth(), 1);
  return c;
}

export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

export function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}

/**
 * Tooltip for a manually-corrected species tag: what a human picked (with
 * attribution when we have it) plus what the AI model originally derived.
 * Assumes the analysis actually carries an override (isManuallyTagged).
 */
export function manualTagTooltip(a: Analysis): string {
  const manualLabel = a.speciesOverride ? SPECIES_LABEL[a.speciesOverride] : "Untagged";
  const orig = aiSpecies(a);
  const origLabel = orig ? SPECIES_LABEL[orig] : "nothing detected";
  let who = "";
  if (a.speciesOverrideBy) {
    who = ` by ${a.speciesOverrideBy}`;
    if (a.speciesOverrideAt) {
      const t = new Date(a.speciesOverrideAt);
      who += ` · ${fmtDate(t)} ${fmtTime(t)}`;
    }
  }
  return `Manually tagged ${manualLabel}${who} — AI detected: ${origLabel}`;
}
